import PropTypes from 'prop-types';
import React from 'react';

import {
    Carousel,
    CarouselHeadingSmall,
    CarouselItems,
    CarouselNavigation
} from '$partials/base/carousel.jsx';

import {
    CarouselItemDispatcher,
    getCssClassForItemType
} from '$partials/carousels';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ModuleMediaCarouselBlock = ({
    heading = '',
    mediaItems = null,
    isAccordionOrSection,
    isFirstInAccordion
}) => {
    // Config
    const carouselConfig = {
        slidesPerView: 'auto',
        spaceBetween: null
    };

    const items = mediaItems.map((item, idx) => {
        if (item.__typename) {
            item.page = item.media;

            if (item.page?.image) {
                if (item.page.image.height === item.page.image.width) {
                    item.page.__typename = item.__typename + 'Square';
                } else if (item.page.image.height > item.page.image.width) {
                    item.page.__typename = item.__typename + 'Portrait';
                } else {
                    item.page.__typename = item.__typename + 'Landscape';
                }
            }

            item.page.carouselItemIndex = idx;
            item.page.slideClassName = getCssClassForItemType(item.page.__typename, idx);

            return (
                <CarouselItemDispatcher
                    { ...item.page }
                    key={idx} />
            );
        }
    }).filter(Boolean);

    items.unshift(
        <CarouselItemDispatcher
            { ...{
                __typename: 'NA',
                slideClassName: 'hidden md:block md:w-1/12',
                key: '-1'
            } }/>
    );

    // Render
    return (
        <section className='relative'>
            <div className='layout-container'>
                <div className='layout-grid-negative-gutter flex'>
                    <div className='md:w-1/12' />
                    <div className='w-12/12 md:w-11/12 layout-grid-col-padding'>
                        <div className={`${isAccordionOrSection && !isFirstInAccordion ? 'border-t border-t-charcoal' : ''}`} />
                    </div>
                </div>
            </div>

            <section className={`layout-container relative mt-40 mb-60 ${isAccordionOrSection ? 'md:my-60' : 'md:mt-60 md:mb-100'} overflow-hidden`}>
                <Carousel swiperConfig={carouselConfig}>
                    <div
                        aria-label={heading ?? null}
                        aria-roledescription='carousel'
                        role='region'>
                        <div className='layout-grid-negative-gutter'>
                            <div className='md:flex flex-wrap items-end pb-40 md:pb-50'>
                                <div className='w-1/12' />

                                <div className={`w-12/12 sm:w-8/12 md:w-8/12 layout-grid-col-padding${items.length > 1 ? ' mb-20 md:mb-0' : ''}`}>
                                    {heading && (
                                        <CarouselHeadingSmall>
                                            {heading}
                                        </CarouselHeadingSmall>
                                    )}
                                </div>

                                <div className='flex gap-20 layout-grid-col-padding ml-auto'>
                                    {items.length > 1 && (
                                        <CarouselNavigation />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='layout-grid-negative-gutter'>
                            <CarouselItems items={items} />
                        </div>
                    </div>
                </Carousel>
            </section>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    heading: PropTypes.string,
    mediaItems: PropTypes.array.isRequired,
    isAccordionOrSection: PropTypes.bool,
    isFirstInAccordion: PropTypes.bool,
};

ModuleMediaCarouselBlock.propTypes = propTypes;

export default ModuleMediaCarouselBlock;
